import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { authAPI, notificationAPI } from '@/lib/api';
import { SocketNotification, WorkoutUpdate } from '@/types';
import toast from 'react-hot-toast';

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  /** Ids of users currently connected, for the online dots. */
  onlineUserIds: Set<string>;
  isUserOnline: (userId?: string | null) => boolean;
  notifications: SocketNotification[];
  /** Unread count for the bell badge. */
  unreadCount: number;
  /** Call when the bell opens: everything shown counts as seen. */
  markNotificationsRead: () => void;
  joinWorkout: (workoutId: string) => void;
  leaveWorkout: (workoutId: string) => void;
  sendWorkoutUpdate: (update: WorkoutUpdate) => void;
  sendPostLiked: (postId: string, postOwnerId: string) => void;
  sendPostCommented: (postId: string, postOwnerId: string, comment: string) => void;
  sendGoalCompleted: (goalId: string, goalTitle: string) => void;
  sendSetCompleted: (exerciseName: string, setNumber: number, reps: number, weight?: number) => void;
  sendWorkoutEnded: () => void;
  clearNotifications: () => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

interface SocketProviderProps {
  children: React.ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const refreshingRef = useRef(false);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUserIds, setOnlineUserIds] = useState<Set<string>>(new Set());
  const [notifications, setNotifications] = useState<SocketNotification[]>([]);
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated && user) {
      // Get token for socket authentication
      const token = localStorage.getItem('accessToken');
      
      if (token) {
        // Create socket connection
        const socketUrl = import.meta.env.VITE_API_URL
          ? import.meta.env.VITE_API_URL.replace('/api', '')
          : 'http://localhost:5000';

        const newSocket = io(socketUrl, {
          // A function, not a fixed value: socket.io calls it on every connect
          // attempt, so a reconnect after the 15-minute access token expired
          // (or after the server slept) picks up the refreshed token instead of
          // retrying forever with a stale one and showing "Offline".
          auth: (cb: (data: { token: string | null }) => void) =>
            cb({ token: localStorage.getItem('accessToken') }),
          withCredentials: true,
        });

        // Connection event handlers
        newSocket.on('connect', () => {
          console.log('🔌 Connected to FitTrack real-time server');
          setIsConnected(true);
        });

        newSocket.on('connected', (data) => {
          console.log('✅ Socket authenticated:', data);
        });

        newSocket.on('disconnect', (reason) => {
          console.log('🔌 Disconnected from server:', reason);
          setIsConnected(false);
          // Nobody can be shown as online over a dead connection.
          setOnlineUserIds(new Set());
        });

        // The roster on arrival, then one message per change.
        newSocket.on('presence:list', (data: { userIds: string[] }) => {
          setOnlineUserIds(new Set(data?.userIds ?? []));
        });

        newSocket.on('presence:update', (data: { userId: string; online: boolean }) => {
          if (!data?.userId) return;
          setOnlineUserIds(prev => {
            const next = new Set(prev);
            if (data.online) next.add(data.userId); else next.delete(data.userId);
            return next;
          });
        });

        newSocket.on('connect_error', async (error) => {
          console.error('❌ Socket connection error:', error);
          setIsConnected(false);

          // An expired token can't fix itself by retrying — refresh once, then
          // let socket.io reconnect with the new one.
          if (/auth|token/i.test(error?.message || '') && !refreshingRef.current) {
            refreshingRef.current = true;
            try {
              const res: any = await authAPI.refreshToken();
              const fresh = res?.data?.accessToken;
              // Store it, or the next reconnect reads the same expired token again.
              if (fresh) localStorage.setItem('accessToken', fresh);
            } catch {
              // Still signed out — the auth layer handles redirecting.
            } finally {
              refreshingRef.current = false;
            }
          }
        });

        // Notification handlers
        newSocket.on('notification', (notification: SocketNotification) => {
          console.log('🔔 New notification:', notification);
          
          // Add to notifications list
          setNotifications(prev => [
            { ...notification, read: false },
            // A re-follow replaces the earlier notice server-side; mirror that here.
            ...prev.filter(n => !(notification.id && n.type === 'follow' && n.from?.id === notification.from?.id)),
          ].slice(0, 50)); // Keep last 50
          
          // Show toast notification
          switch (notification.type) {
            case 'like':
              toast.success(notification.message, {
                icon: '❤️',
              });
              break;
            case 'comment':
              toast.success(notification.message, {
                icon: '💬',
              });
              break;
            case 'follow':
              toast.success(notification.message, {
                icon: '👥',
              });
              break;
            case 'goal_achievement':
              toast.success(notification.message, {
                icon: '🎯',
                duration: 6000,
              });
              break;
            default:
              toast.success(notification.message);
          }
        });

        // Workout update handlers
        newSocket.on('workout-updated', (data) => {
          console.log('💪 Workout updated:', data);
          // This could be used for shared workouts or real-time collaboration
        });

        newSocket.on('workout-update-ack', (data) => {
          console.log('✅ Workout update acknowledged:', data);
        });

        // Error handler
        newSocket.on('error', (error) => {
          console.error('❌ Socket error:', error);
          // Don't show raw socket errors to users — just log them silently
        });

        // Heartbeat pulse handler - friend is logging a set
        newSocket.on('heartbeat-pulse', (data) => {
          console.log('💓 Heartbeat pulse from friend:', data);
          // Emit custom event that components can listen to
          window.dispatchEvent(new CustomEvent('heartbeat-pulse', { detail: data }));
          
          // Show subtle toast
          toast.success(`${data.username} just logged ${data.reps} reps of ${data.exerciseName}!`, {
            icon: '💓',
            duration: 3000,
          });
        });

        setSocket(newSocket);

        // Cleanup on unmount
        return () => {
          newSocket.close();
        };
      }
    } else {
      // Disconnect socket if not authenticated
      if (socket) {
        socket.close();
        setSocket(null);
        setIsConnected(false);
      }
    }
  }, [isAuthenticated, user]);

  // Socket utility functions
  const joinWorkout = (workoutId: string) => {
    if (socket && isConnected) {
      socket.emit('join-workout', workoutId);
    }
  };

  const leaveWorkout = (workoutId: string) => {
    if (socket && isConnected) {
      socket.emit('leave-workout', workoutId);
    }
  };

  const sendWorkoutUpdate = (update: WorkoutUpdate) => {
    if (socket && isConnected) {
      socket.emit('workout-update', update);
    }
  };

  const sendPostLiked = (postId: string, postOwnerId: string) => {
    if (socket && isConnected) {
      socket.emit('post-liked', { postId, postOwnerId });
    }
  };

  const sendPostCommented = (postId: string, postOwnerId: string, comment: string) => {
    if (socket && isConnected) {
      socket.emit('post-commented', { postId, postOwnerId, comment });
    }
  };

  const sendGoalCompleted = (goalId: string, goalTitle: string) => {
    if (socket && isConnected) {
      socket.emit('goal-completed', { goalId, goalTitle });
    }
  };

  const sendSetCompleted = (exerciseName: string, setNumber: number, reps: number, weight?: number) => {
    if (socket && isConnected) {
      socket.emit('set-completed', { exerciseName, setNumber, reps, weight });
    }
  };

  const sendWorkoutEnded = () => {
    if (socket && isConnected) {
      socket.emit('workout-ended');
    }
  };

  // Stored notifications (e.g. new followers) that arrived while this person
  // was away. Live ones are merged in by the socket handler above.
  useEffect(() => {
    if (!isAuthenticated || !user) {
      setNotifications([]);
      return;
    }
    let cancelled = false;
    notificationAPI.list()
      .then((res: any) => {
        if (cancelled) return;
        const saved: SocketNotification[] = res?.data?.notifications ?? [];
        setNotifications(prev => {
          // Keep anything that arrived live while the list was loading.
          const savedIds = new Set(saved.map(n => n.id));
          return [...prev.filter(n => !n.id || !savedIds.has(n.id)), ...saved].slice(0, 50);
        });
      })
      .catch(() => { /* bell just starts empty */ });
    return () => { cancelled = true; };
  }, [isAuthenticated, user?.id]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const markNotificationsRead = () => {
    if (unreadCount === 0) return;
    setNotifications(prev => prev.map(n => (n.read ? n : { ...n, read: true })));
    notificationAPI.markAllRead().catch(() => { /* badge reappears on next load */ });
  };

  const clearNotifications = () => {
    setNotifications([]);
    notificationAPI.clearAll().catch(() => { /* they'll reload next visit */ });
  };

  const isUserOnline = (userId?: string | null) => !!userId && onlineUserIds.has(userId);

  const value: SocketContextType = {
    socket,
    isConnected,
    onlineUserIds,
    isUserOnline,
    notifications,
    unreadCount,
    markNotificationsRead,
    joinWorkout,
    leaveWorkout,
    sendWorkoutUpdate,
    sendPostLiked,
    sendPostCommented,
    sendGoalCompleted,
    sendSetCompleted,
    sendWorkoutEnded,
    clearNotifications,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};